import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router';
import { RequireAuth, Landing, NoAccess, ShopPicker } from './auth/Guards';
import { SignIn } from './auth/SignIn';
import { isPlatformAdmin, shopIdsFor } from './lib/roles.js';
import { AuthPage, ComingSoon, EmptyState, useTitle } from './ui/bits';
import { STATE_ART } from './lib/voice.js';
import { OpsLayout } from './ops/OpsLayout';
import { LiveOrdersPage, OpsOverviewPage } from './ops/Overview';
import { RidersPage } from './ops/riders';
import { OpsShopDetailPage, OpsShopNewPage, ShopsPage } from './ops/shops';
import { OpsTeamPage } from './ops/team';
import { SettingsPage } from './ops/Settings';
import { ExpressPage } from './ops/express/ExpressPage';
import { OpsCustomersPage } from './ops/customers';
import { OpsCataloguePage } from './ops/catalogue';
import { OpsPaymentsPage } from './ops/payments';
import { OpsCatchWeightPage } from './ops/catchweight';
import { OpsRefundsPage } from './ops/refunds';
import { OpsPayoutsPage } from './ops/payouts';
import { OpsZonesPage } from './ops/zones';
import { OpsReportsPage } from './ops/reports';
import { OpsMapHealthPage } from './ops/maphealth';
import { OpsAdsPage } from './ops/ads';
import { ShopLayout } from './shop/ShopLayout';
import { TodayPage } from './shop/Today';
import { OrdersPage } from './shop/Orders';
import { ProductsPage } from './shop/products/ProductsPage';
import { ProductEditPage } from './shop/products/ProductEditPage';
import { OpsProductsPage } from './ops/products/OpsProductsPage';
import { OpsProductEditPage } from './ops/products/OpsProductEditPage';
import { CategoriesPage } from './ops/categories/CategoriesPage';
import { FreshPage } from './shop/fresh/FreshPage';
import { GrillPage } from './shop/grill/GrillPage';
import { MartPage } from './shop/mart/MartPage';
import { ShopHandoffPage } from './shop/handoff';
import { ShopHoursPage } from './shop/hours';
import { ShopStaffPage } from './shop/staff';
import { ShopReviewsPage } from './shop/reviews';
import { ShopSalesPage } from './shop/sales';

// Nav items from the approved mockups that aren't built yet: a "Not here yet" state (BO-8).
const OPS_LATER: { path: string; title: string; note?: string }[] = [];

const SHOP_LATER: { path: string; title: string }[] = [];

function NotFound() {
  useTitle('Not found');
  return (
    <AuthPage>
      <EmptyState art={STATE_ART.notFound} title="This page popped off" action={<Link className="btn btn-primary" to="/">Back to the back office</Link>}>
        Wrong turn, or it moved. Let's get you back.
      </EmptyState>
    </AuthPage>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/no-access" element={<RequireAuth><NoAccess /></RequireAuth>} />
        <Route
          path="/shops"
          element={
            <RequireAuth allow={(me) => shopIdsFor(me).length > 0 || isPlatformAdmin(me)}>
              <ShopPicker />
            </RequireAuth>
          }
        />
        <Route
          path="/ops"
          element={
            <RequireAuth allow={isPlatformAdmin}>
              <OpsLayout />
            </RequireAuth>
          }
        >
          <Route index element={<OpsOverviewPage />} />
          <Route path="live" element={<LiveOrdersPage />} />
          <Route path="riders" element={<RidersPage />} />
          <Route path="shops" element={<ShopsPage />} />
          <Route path="shops/new" element={<OpsShopNewPage />} />
          <Route path="shops/:shopRef" element={<OpsShopDetailPage />} />
          <Route path="team" element={<OpsTeamPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="express" element={<ExpressPage />} />
          <Route path="products" element={<OpsProductsPage />} />
          <Route path="products/new" element={<OpsProductEditPage />} />
          <Route path="products/:shopRef/:productId" element={<OpsProductEditPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="customers" element={<OpsCustomersPage />} />
          <Route path="catalogue" element={<OpsCataloguePage />} />
          <Route path="payments" element={<OpsPaymentsPage />} />
          <Route path="catch-weight" element={<OpsCatchWeightPage />} />
          <Route path="refunds" element={<OpsRefundsPage />} />
          <Route path="payouts" element={<OpsPayoutsPage />} />
          <Route path="zones" element={<OpsZonesPage />} />
          <Route path="reports" element={<OpsReportsPage />} />
          <Route path="maps" element={<OpsMapHealthPage />} />
          <Route path="ads" element={<OpsAdsPage />} />
          {OPS_LATER.map((p) => (
            <Route key={p.path} path={p.path} element={<ComingSoon title={p.title}>{p.note}</ComingSoon>} />
          ))}
        </Route>
        <Route
          path="/shop/:shopRef"
          element={
            <RequireAuth>
              <ShopLayout />
            </RequireAuth>
          }
        >
          <Route index element={<TodayPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="products/:productId" element={<ProductEditPage />} />
          {/* The old Stock page lives inside Products & stock now. */}
          <Route path="stock" element={<Navigate to="../products" replace />} />
          <Route path="fresh" element={<FreshPage />} />
          <Route path="grill" element={<GrillPage />} />
          <Route path="mart" element={<MartPage />} />
          <Route path="handoff" element={<ShopHandoffPage />} />
          <Route path="hours" element={<ShopHoursPage />} />
          <Route path="staff" element={<ShopStaffPage />} />
          <Route path="reviews" element={<ShopReviewsPage />} />
          <Route path="sales" element={<ShopSalesPage />} />
          {SHOP_LATER.map((p) => (
            <Route key={p.path} path={p.path} element={<ComingSoon title={p.title} scope="shop" />} />
          ))}
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
